import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import Brand from "../components/Brand";
import RiskCheckIcon from "../components/RiskCheckIcon";
import SbrFooter from "../components/SbrFooter";
import SiteNav from "../components/SiteNav";
import Plate from "../components/Plate";
import RecentRegChips from "../components/RecentRegChips";
import usePageMeta from "../hooks/usePageMeta";
import { LOOKUP_STEP_MS, cleanRegistration, formatUkRegistration, isValidUkRegistration, lookupSteps } from "../utils/registration";
import { completeAssuranceByBrand, packages } from "../config/pricing.js";
import ServiceLadLanding from "../brands/servicelad/ServiceLadLanding";
import { goToNavHref, guideLink } from "../utils/nav";
import { serviceBossHomeMeta } from "../../lib/serviceBossSeo.js";
import { SERVICE_HISTORY_PATH, hasServiceHistoryPages } from "../content/serviceHistoryRoutes";

const features = [
  ["01", "▤", "Service timeline", "See recorded service events in chronological order, from the first visit to the latest."],
  ["02", "⌁", "Mileage verification", "Compare mileage recorded at each service and spot inconsistencies before they cost you."],
  ["03", "⌖", "Garage details", "Find the dealership or service centre behind each available maintenance record."],
  ["04", "⚙", "Work carried out", "Oil, filters, fluids, plugs and more, see itemised actions when the manufacturer holds them."],
  ["05", "◇", "VIN confirmation", "Match the report to a partially masked VIN for an added layer of vehicle confidence."],
  ["06", "↓", "Report ready to keep", "A clean digital summary you can review on any device when viewing or selling a vehicle."],
];

// ServiceByReg only, see the "features" section below: a risk-check audit
// ledger, not the shared numbered card grid the other 2 brands use. Still
// a service-history report first, same as the other 2 brands, with an HPI-
// style risk check alongside it, not instead of it, so 5 of the 6 rows are
// the service-record detail every brand leads with, and the risk check is
// the "plus" item, not the whole page.
const riskFeatures = [
  { id: "service-history", title: "Full service history report", copy: "Every dated, dealer-recorded service and the mileage logged at each visit, from first visit to latest." },
  { id: "stolen", title: "Stolen vehicle check", copy: "Cross-checked against the police register before you view the car." },
  { id: "write-off", title: "Insurance write-off check", copy: "Flagged if the vehicle's been written off and repaired." },
  { id: "mileage", title: "Mileage verification", copy: "Compared across recorded services, not taken on the seller's word." },
  { id: "garage", title: "Garage details", copy: "Find the dealership or service centre behind each available maintenance record." },
  { id: "work", title: "Work carried out", copy: "Oil, filters, fluids, plugs and more, itemised when the manufacturer holds them." },
  { id: "vin", title: "VIN confirmation", copy: "Match the report to a partially masked VIN for an added layer of confidence." },
  { id: "finance", title: "Outstanding finance check", copy: "Flagged if there's still money owed on the vehicle." },
];

// Row-by-row detail for the risk-ledger modal. Kept specific to each check,
// not a reworded copy of the row itself, so the modal earns the click.
const riskDetails = [
  { tag: "SERVICE RECORD", body: "We pull every dealer-logged service event tied to the registration, not just the last one. Each entry carries a date and the mileage recorded at that visit, so you can see the car's maintenance rhythm from new.", why: "A gap of two or three years between visits, or a service missing entirely, is one of the strongest predictors of expensive faults turning up within 12 months of buying." },
  { tag: "RISK CHECK", body: "The registration is checked against the police-held stolen vehicle register before you view the car, not after you've put down a deposit.", why: "If a car turns out to be stolen, ownership reverts to its rightful owner by law, even if you bought it in good faith. You lose the car and the money." },
  { tag: "RISK CHECK", body: "We surface the ABI damage category where one exists, Cat S (structural damage, repaired) or Cat N (non-structural, repaired), so you know exactly what the car has been through.", why: "A repaired Cat S car can drop 20-30% in resale value, and some insurers will decline to cover it at all. None of that shows up on a logbook." },
  { tag: "SERVICE RECORD", body: "Every mileage figure across the service history is compared against the others in sequence, not taken at face value from the seller or the odometer alone.", why: "Clocking, winding back the odometer, remains one of the most common used-car frauds in the UK. A record where mileage goes backwards is the tell." },
  { tag: "SERVICE RECORD", body: "Where the manufacturer holds it, we show which dealership or approved centre carried out each service, not just that a service happened.", why: "A car serviced consistently at franchised dealers usually means genuine parts and a warranty kept intact, worth knowing before you negotiate on price." },
  { tag: "SERVICE RECORD", body: "Itemised actions where the manufacturer's record breaks the visit down that far: oil, filters, brake fluid, cambelt, spark plugs.", why: "Two cars can show the same service dates but very different work. One's had its cambelt changed on schedule, the other's overdue and one pothole from an engine rebuild." },
  { tag: "SERVICE RECORD", body: "We match the report to a partially masked VIN, so you can confirm it lines up with the plate on the car's door jamb or chassis before you commit.", why: "VIN cloning, giving a stolen car the identity of a legitimate one, relies on buyers never checking. A three-second glance at the door jamb closes that gap." },
  { tag: "RISK CHECK", body: "We check whether there's an active HP or PCP agreement still registered against the vehicle.", why: "A car on finance technically belongs to the lender until it's settled. Buy one and the finance company can repossess it from you, even though you paid the seller in full." },
];

const completeReportIcons = [
  "/svgs/taxprice.svg",
  "/svgs/service-wrench.svg",
  "/svgs/engine.svg",
  "/svgs/print.svg",
];

const events = [
  ["08 JUL 2021", "Scheduled service", "10,421 mi"],
  ["20 SEP 2022", "Oil, filter and brake fluid", "27,631 mi"],
  ["03 OCT 2023", "Major service", "39,550 mi"],
  ["17 OCT 2024", "Scheduled service", "46,955 mi"],
];

// Used only by ServiceByReg's own custom hero nav below (the other 2 brands
// use the shared SiteNav component instead). Kept identical in order and
// subset to SiteNav.jsx's serviceByRegLinks, this brand drops Sample report
// and What's included, its own product is the risk check itself.
const taxToolLink = (brandKey) => ({
  label: brandKey === "servicebyreg" ? "Road tax check" : brandKey === "servicelad" ? "Check road tax" : "Tax calculator",
  href: "/car-tax-calculator/",
});

const baseNavLinks = [
  { label: "Check a vehicle", href: "/#lookup", primary: true },
  { label: "Pricing", href: "/pricing" },
  { label: "Insurance group", href: "/insurance-groups" },
  { label: "V5C alerts", href: "/v5c-alerts" },
  { label: "Free car check", href: "/free-car-check" },
];

function navLinksFor(brandKey) {
  const links = [...baseNavLinks];
  links.splice(4, 0, taxToolLink(brandKey));
  return links;
}

const moreLinks = [
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "Terms", href: "/terms" },
  { label: "Privacy", href: "/privacy" },
];

export default function HomePage({ brand }) {
  if (brand.key === "servicelad") {
    return <ServiceLadLanding brand={brand} />;
  }
  return <SharedHomePage brand={brand} />;
}

function SharedHomePage({ brand }) {
  const [vrm, setVrm] = useState("");
  const [error, setError] = useState("");
  const [showServiceHistory, setShowServiceHistory] = useState(false);
  const [activeRisk, setActiveRisk] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);

  usePageMeta({
    title: brand.key === "servicebyreg"
      ? "ServiceByReg: Check the risks. Before you buy."
      : brand.key === "servicelad"
        ? "Cheapest Full HPI Check £7.95, Free MOT and Tax | ServiceLad"
        : serviceBossHomeMeta.title,
    ogDescription: brand.key === "serviceboss" ? serviceBossHomeMeta.ogDescription : undefined,
    twitterDescription: brand.key === "serviceboss" ? serviceBossHomeMeta.twitterDescription : undefined,
    description: brand.key === "servicebyreg"
      ? "Enter a UK registration to check stolen markers, write-off category and outstanding finance before you buy. Free tax and MOT first; add the risk report when you need it."
      : brand.key === "servicelad"
        ? "See tax, MOT and key changes on a car you already own. Free lookups first, with optional V5C logbook alerts so nothing important slips by."
        : serviceBossHomeMeta.description,
    canonical: `https://${brand.domain}/`,
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
          observer.unobserve(entry.target);
        }
      }),
      { threshold: 0.12 },
    );
    document.querySelectorAll(".reveal").forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", onClick);
    return () => document.removeEventListener("pointerdown", onClick);
  }, [menuOpen]);

  // Hero lookup state: the reg being opened and which loading step to show.
  const [lookingUp, setLookingUp] = useState("");
  const [lookupStep, setLookupStep] = useState(0);
  useEffect(() => {
    if (!lookingUp) return undefined;
    const timer = window.setInterval(() => setLookupStep((step) => Math.min(step + 1, 2)), LOOKUP_STEP_MS);
    return () => window.clearInterval(timer);
  }, [lookingUp]);
  useEffect(() => {
    // Coming back with the browser back button restores the idle button.
    const onShow = (event) => { if (event.persisted) { setLookingUp(""); setLookupStep(0); } };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  const submitVrm = (event, plan) => {
    event?.preventDefault();
    const cleanVrm = cleanRegistration(vrm);
    if (!isValidUkRegistration(cleanVrm)) {
      setError("Enter a valid UK registration to continue.");
      document.querySelector("#vrm")?.focus();
      return;
    }
    // Homepage search opens the free vehicle page first on all brands.
    // Pricing buttons still pass a plan and go straight to checkout.
    if (!plan) {
      if (lookingUp) return;
      setLookingUp(cleanVrm);
      setLookupStep(0);
      window.location.href = `/vehicle/${cleanVrm.toLowerCase()}`;
      return;
    }
    const query = new URLSearchParams({ vrm: cleanVrm });
    if (plan) query.set("plan", plan);
    window.location.href = `/checkout?${query}`;
  };

  const allLinks = [...navLinksFor(brand.key), ...guideLink(brand), ...(hasServiceHistoryPages(brand.key) ? [{ label: "Service history check", href: SERVICE_HISTORY_PATH }] : []), ...moreLinks];

  const handleNav = (href) => {
    setMenuOpen(false);
    goToNavHref(href);
  };

  return (
    <>
      {brand.key !== "servicebyreg" && <SiteNav brand={brand} />}

      <main>
        {brand.key === "servicebyreg" ? (
          <section className="sbr-hero" id="lookup">
            <div className="sbr-nav-wrap" ref={navRef}>
              <div className="wrap sbr-hero-nav">
                <Brand brand={brand} />
                <button
                  className={`menu-trigger${menuOpen ? " is-open" : ""}`}
                  type="button"
                  onClick={() => setMenuOpen((o) => !o)}
                  aria-expanded={menuOpen}
                  aria-controls="sbr-menu"
                  aria-label={menuOpen ? "Close menu" : "Open menu"}
                >
                  <span className="trigger-burger" aria-hidden="true">
                    <i /><i /><i />
                  </span>
                </button>
                <div
                  className={`nav-panel${menuOpen ? " is-open" : ""}`}
                  id="sbr-menu"
                >
                  <nav className="nav-panel-links" aria-label="Main navigation">
                    {allLinks.map((link) => (
                      <button
                        key={link.label}
                        className={`nav-panel-link${link.primary ? " is-primary" : ""}`}
                        onClick={() => handleNav(link.href)}
                        type="button"
                      >
                        <span>{link.label}</span>
                        <span className="nav-link-arrow" aria-hidden="true">→</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            <div className="wrap sbr-hero-grid">
              <div className="sbr-hero-headline">
                <h1 className="sbr-headline">
                  <span className="sbr-headline-bold">Check any vehicle.</span>
                  <span className="sbr-headline-light">Know more.</span>
                </h1>
                <ul className="sbr-hero-info-rows" aria-hidden="true">
                  <li className="sbr-info-row">
                    <span className="sbr-info-icon">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l8 4v5c0 5-3.3 8-8 9-4.7-1-8-4-8-9V7l8-4Zm-3 9 2 2 4-5" /></svg>
                    </span>
                    <span className="sbr-info-text">
                      <strong>Full vehicle checks</strong>: stolen status, outstanding finance, write-offs, mileage, specifications and more.
                    </span>
                  </li>
                  <li className="sbr-info-row">
                    <span className="sbr-info-icon">
                      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 3h7l5 5v13H7zM14 3v5h5M10 13h6M10 17h6" /></svg>
                    </span>
                    <span className="sbr-info-text">
                      <strong>Service history by registration</strong>: available for many popular car brands, mainly vehicles from 2012 onwards.
                    </span>
                  </li>
                </ul>
              </div>
              <div className="sbr-hero-visual">
                <picture className="sbr-hero-picture">
                  <source
                    type="image/avif"
                    srcSet="/pg-hero-900.avif 900w, /pg-hero.avif 1590w"
                    sizes="(max-width: 620px) 108vw, (max-width: 900px) 105vw, (min-width: 1500px) 1080px, 72vw"
                  />
                  <source
                    type="image/webp"
                    srcSet="/pg-hero-900.webp 900w, /pg-hero.webp 1590w"
                    sizes="(max-width: 620px) 108vw, (max-width: 900px) 105vw, (min-width: 1500px) 1080px, 72vw"
                  />
                  <img
                    src="/pg-hero.webp"
                    srcSet="/pg-hero-900.webp 900w, /pg-hero.webp 1590w"
                    sizes="(max-width: 620px) 108vw, (max-width: 900px) 105vw, (min-width: 1500px) 1080px, 72vw"
                    alt="Blue Peugeot 308 with vehicle history check graphics"
                    className="sbr-hero-car"
                    width="1590"
                    height="989"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                </picture>
                <div className="sbr-mobile-insight" aria-hidden="true">
                  <div className="sbr-insight-slide">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3v3M18 3v3M4 9h16M5 5h14a1 1 0 0 1 1 1v13H4V6a1 1 0 0 1 1-1Z" /></svg>
                    <div><strong>Service history</strong><span>See recorded dates and work.</span></div>
                  </div>
                  <div className="sbr-insight-slide">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 17l4-5 4 2 5-7 3 2M4 20h16" /></svg>
                    <div><strong>Mileage checks</strong><span>Spot gaps and unusual changes.</span></div>
                  </div>
                  <div className="sbr-insight-slide">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3l8 4v5c0 5-3.3 8-8 9-4.7-1-8-4-8-9V7l8-4Zm-3 9 2 2 4-5" /></svg>
                    <div><strong>Risk records</strong><span>Check finance, theft and write-offs.</span></div>
                  </div>
                  <div className="sbr-insight-slide">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
                    <div><strong>Clear results</strong><span>Key records in one simple report.</span></div>
                  </div>
                </div>
              </div>
              <div className="sbr-hero-form">
                <form className="sbr-reg-form" onSubmit={submitVrm} noValidate>
                  <div className="sbr-reg-card">
                    <div className="sbr-reg-field">
                      <span className="sbr-plate-badge" aria-label="United Kingdom">
                        <img src="/gb-flag.svg" alt="" width="20" height="20" />
                        <span className="sbr-plate-country">UK</span>
                      </span>
                      <label className="sr-only" htmlFor="vrm" style={{ position: "absolute", clip: "rect(0,0,0,0)" }}>Vehicle registration</label>
                      <input
                        className="sbr-reg-input"
                        id="vrm"
                        maxLength="8"
                        placeholder="Enter registration"
                        autoComplete="off"
                        value={vrm}
                        onChange={(event) => {
                          setVrm(cleanRegistration(event.target.value));
                          setError("");
                        }}
                      />
                    </div>
                    <button className={`sbr-reg-submit${lookingUp ? " is-loading" : ""}`} type="submit" disabled={Boolean(lookingUp)} aria-busy={lookingUp ? "true" : undefined}>
                      {lookingUp ? (
                        <span className="sbr-reg-step" key={lookupStep} role="status">
                          {lookupSteps(formatUkRegistration(lookingUp))[lookupStep]}
                        </span>
                      ) : "Check Now"}
                      <span className="sbr-reg-arrow" aria-hidden="true">
                        {lookingUp ? <span className="sbr-reg-spinner" /> : (
                          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="white" strokeWidth="2" aria-hidden="true">
                            <path d="M5 12h14M13 6l6 6-6 6" />
                          </svg>
                        )}
                      </span>
                    </button>
                  </div>
                </form>
                <RecentRegChips
                  brandKey="sbr"
                  variant="sbr"
                  onSelect={(reg) => {
                    const clean = cleanRegistration(reg);
                    setVrm(clean);
                    setError("");
                    window.location.href = `/vehicle/${clean.toLowerCase()}`;
                  }}
                />
                <div className="sbr-form-message" aria-live="polite">{error}</div>
              </div>
            </div>

            <div className="wrap sbr-hero-trust">
              <div className="sbr-trust-item">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M12 2L4 5v6c0 5 3.5 9 8 11 4.5-2 8-6 8-11V5l-8-3z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
                <span>Secure &amp; private</span>
              </div>
              <div className="sbr-trust-item">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
                </svg>
                <span>Instant results</span>
              </div>
              <div className="sbr-trust-item">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M8 12l3 3 5-6" />
                </svg>
                <span>Trusted data</span>
              </div>
            </div>
          </section>
        ) : (
          <section className="hero" id="lookup">
            <div className="wrap hero-inner">
              <div className="hero-copy">
                <div className="eyebrow">{brand.key === "servicebyreg" ? "Pre-purchase risk checks" : brand.key === "servicelad" ? "Stay ahead of your car" : "Manufacturer service records"}</div>
                <h1>{brand.key === "servicebyreg" ? <>Check the risks.<br /><em>Before you buy.</em></> : brand.key === "servicelad" ? <>Know what changed.<br /><em>Stay ahead.</em></> : <>Know the history.<br /><em>Own the road.</em></>}</h1>
                <p>{brand.key === "servicebyreg" ? "Reveal a vehicle's service history before you buy. Dates, mileage, garages and work carried out, in one clear report." : brand.key === "servicelad" ? "A clearer view of the records behind your car, so every service, mileage reading and change is easy to follow." : "Reveal manufacturer-held service records before you buy. Dates, mileage, garages and work carried out, in one clear report."}</p>
                <form className="vrm-form" onSubmit={submitVrm} noValidate>
                  <div className="plate-wrap">
                    <span className="plate-band" aria-hidden="true"><img src="/gb-flag.svg" alt="" /><b>UK</b></span>
                    <label className="sr-only" htmlFor="vrm" style={{ position: "absolute", clip: "rect(0,0,0,0)" }}>Vehicle registration</label>
                    <input
                      className="vrm-input"
                      id="vrm"
                      maxLength="8"
                      placeholder="ENTER REG"
                      autoComplete="off"
                      value={vrm}
                      onChange={(event) => {
                        setVrm(cleanRegistration(event.target.value));
                        setError("");
                      }}
                    />
                  </div>
                  <button className="vrm-submit" type="submit">Check my vehicle →</button>
                </form>
                <div className="form-note">No subscription. Secure one-off payment. Coverage applies.</div>
                <div className="form-message" aria-live="polite">{error}</div>
              </div>
              <div className="hero-visual">
                {brand.key === "servicelad" && (
                  <button className="hero-visual-badge" type="button" onClick={() => setShowServiceHistory(true)} aria-label="What is included in ServiceLad history">
                    <span>●</span> SERVICE HISTORY <b>↗</b>
                  </button>
                )}
                <picture>
                  <source srcSet={brand.key === "servicelad" ? "/service-lad-sky.webp" : "/a1-service.webp"} type="image/webp" />
                  <img
                    src={brand.key === "servicelad" ? "/service-lad-sky.png" : "/a1-service.png"}
                    alt={brand.key === "servicelad" ? "ServiceLad vehicle records illustration" : "Manufacturer service history report for a UK car"}
                    className="hero-car"
                  />
                </picture>
                <p className="hero-visual-text">{brand.key === "servicelad" ? "Every mile leaves a clue. We make it clear." : "Dates, mileage, garages and work carried out, in one clear report."}</p>
              </div>
            </div>
            <div className="hero-proof">
              <div className="wrap proof-inner">
                <div className="proof-item"><span className="proof-icon">✓</span> Official manufacturer data</div>
                <div className="proof-item"><span className="proof-icon">⌁</span> Instant digital report</div>
                <div className="proof-item"><span className="proof-icon">◇</span> Secure checkout</div>
              </div>
            </div>
          </section>
        )}

        <section className="features" id="features">
          <div className="wrap">
            <div className="section-head reveal">
              <div><div className="eyebrow dark-eyebrow">See beyond the logbook</div><h2>The details that make a difference.</h2></div>
              <p>{brand.key === "servicebyreg" ? "Every report checks stolen, write-off and finance. The Complete report adds the car's service history, dates, mileage and work done." : "Get the evidence you need to buy with confidence, negotiate fairly, or protect your car's value."}</p>
            </div>
            {brand.key === "servicebyreg" ? (
              <div className="risk-ledger">
                {riskFeatures.map((feature, i) => (
                  <button
                    type="button"
                    className="risk-row reveal"
                    key={feature.id}
                    style={{ transitionDelay: `${i * 40}ms` }}
                    onClick={() => setActiveRisk(i)}
                  >
                    <div className="risk-badge" aria-hidden="true">
                      <RiskCheckIcon name={feature.id} size="md" />
                    </div>
                    <div className="risk-copy"><h3>{feature.title}</h3><p>{feature.copy}</p></div>
                    <span className="risk-tag"><Check size={14} strokeWidth={2.5} aria-hidden="true" />Checked</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="feature-grid reveal">
                {features.map(([number, icon, title, copy]) => (
                  <article className="feature" key={number}>
                    <span className="feature-number">{number}</span><div className="feature-icon">{icon}</div>
                    <h3>{title}</h3><p>{copy}</p>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="sample" id="sample">
          <div className="wrap sample-grid">
            <div className="sample-copy reveal">
              <div className="eyebrow">Built for clarity</div>
              <h2>{brand.key === "servicebyreg" ? <>What&rsquo;s in the<br />service history bundle.</> : <>Every service.<br />One clear story.</>}</h2>
              <p>No confusing data dumps. We turn available manufacturer records into a simple timeline that helps you understand how a car has been cared for.</p>
              <ul className="check-list">
                {["Service dates and recorded mileage", "Dealer or garage when available", "Itemised maintenance actions", "Miles and kilometres handled correctly"].map((item) => <li key={item}><span className="tick">✓</span>{item}</li>)}
              </ul>
            </div>
            <div className="report-card reveal">
              <div className="report-top"><Brand brand={brand} /><span className="verified">● REPORT FOUND</span></div>
              <div className="vehicle"><div><h3>{brand.key === "servicelad" ? "Mercedes-Benz GLE 400" : "Mercedes-Benz A-Class"}</h3><p>{brand.key === "servicelad" ? "Diesel · Automatic · 2022" : "Diesel · Automatic · 2020"}</p></div><Plate registration={brand.key === "servicelad" ? "LJ72XXX" : "LM20XXX"} compact /></div>
              <div className="timeline">
                {events.map(([date, service, mileage]) => (
                  <div className="event" key={date}><span className="date">{date}</span><div><strong>{service}</strong><small>Mercedes-Benz of Warrington</small></div><span className="mileage">{mileage}</span></div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="pricing" id="pricing">
          <div className="wrap">
            <div className="section-head reveal jobsheet-head">
              <div><div className="eyebrow dark-eyebrow">Simple, honest pricing</div><h2>Choose the check that fits.</h2></div>
              <p>One-off payments. No subscription, no surprise renewal, no fine print.</p>
            </div>
            {brand.key === "servicebyreg" ? (
              <div className="sbr-document-pricing reveal">
                <article className="sbr-risk-sheet">
                  <div className="sbr-sheet-clip" aria-hidden="true" />
                  <div className="sbr-document-kicker">{packages[0].stamp}</div>
                  <h3>{packages[0].name}</h3>
                  <div className="sbr-document-plate" aria-label={`Price ${packages[0].plate} pounds`}>
                    <span className="sbr-document-plate-band"><img src="/gb-flag.svg" alt="" />UK</span>
                    <strong>£{packages[0].plate}</strong>
                  </div>
                  <p>{packages[0].copy}</p>
                  <ul className="sbr-risk-list">
                    {packages[0].items.map((item) => (
                      <li key={item}><span aria-hidden="true">✓</span>{item}</li>
                    ))}
                  </ul>
                  <button type="button" className="sbr-risk-cta" onClick={() => submitVrm(null, packages[0].name)}>
                    {packages[0].button} <span aria-hidden="true">→</span>
                  </button>
                </article>

                <article className="sbr-service-book">
                  <span className="sbr-book-label">Best value</span>
                  <div className="sbr-book-pages">
                    <div className="sbr-book-page sbr-book-page-left">
                      <div className="sbr-document-kicker">{packages[1].stamp}</div>
                      <h3>{packages[1].name}</h3>
                      <div className="sbr-document-plate" aria-label={`Price ${packages[1].plate} pounds`}>
                        <span className="sbr-document-plate-band"><img src="/gb-flag.svg" alt="" />UK</span>
                        <strong>£{packages[1].plate}</strong>
                      </div>
                      <p>{packages[1].copy}</p>
                      <div className="sbr-book-plus">
                        <span>Everything in Risk Check, plus…</span>
                        <strong><span aria-hidden="true">✓</span>{packages[1].items[0]}</strong>
                      </div>
                    </div>
                    <div className="sbr-book-page sbr-book-page-right">
                      <div className="sbr-book-section-title">Inside the complete report</div>
                      <ul className="sbr-book-records">
                        {packages[1].items.slice(1).map((item, index) => (
                          <li key={item}>
                            <span className="sbr-book-icon" aria-hidden="true">
                              <img src={completeReportIcons[index]} alt="" />
                            </span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="sbr-book-assurance">{completeAssuranceByBrand.servicebyreg}</p>
                    </div>
                  </div>
                  <button type="button" className="sbr-book-cta" onClick={() => submitVrm(null, packages[1].name)}>
                    {packages[1].button} <span aria-hidden="true">→</span>
                  </button>
                </article>
                <div className="sbr-document-foot" aria-hidden="true">V5C logbook monitoring available as an add-on at checkout</div>
              </div>
            ) : (
              <div className="jobsheet reveal">
                <div className="clip" aria-hidden="true"><span /><span /><span /></div>
                <div className="jobsheet-grid">
                  {packages.map((plan) => (
                    <article className={`sheet${plan.featured ? " sheet-featured" : ""}`} key={plan.name}>
                      <div className="sheet-stamp" aria-hidden="true">{plan.stamp}</div>
                      <div className="sheet-head">
                        <span className="sheet-plan">{plan.name}</span>
                        <div className="sheet-plate" aria-label={`Price ${plan.plate} pounds`}>
                          <span className="sheet-plate-band"><img src="/gb-flag.svg" alt="" />UK</span>
                          <strong className="sheet-plate-reg">£{plan.plate}</strong>
                        </div>
                      </div>
                      <p className="sheet-copy">{plan.copy}</p>
                      <ul className="sheet-list">
                        {plan.items.map((item) => (
                          <li key={item}><span className="stamp-box" aria-hidden="true">✓</span>{item}</li>
                        ))}
                      </ul>
                      {plan.featured && <p className="sheet-assurance">{completeAssuranceByBrand[brand.key]}</p>}
                      <button className="sheet-btn" onClick={() => submitVrm(null, plan.name)}>{plan.button} <span aria-hidden="true">→</span></button>
                      {plan.featured && <span className="sheet-flag">Best value</span>}
                    </article>
                  ))}
                </div>
                <div className="jobsheet-foot" aria-hidden="true">V5C logbook monitoring available as an add-on at checkout</div>
              </div>
            )}
          </div>
        </section>

        <section className="trust">
          <div className="wrap trust-inner reveal">
            <div className="trust-copy"><h2>Vehicle history, handled properly.</h2><p>Secure, transparent and built around trusted data sources.</p></div>
            {brand.key === "servicebyreg" ? (
              <div className="sbr-payment-logos" aria-label="Accepted payment methods">
                <span className="sbr-payment-logo"><img src="/payment-logos/apple-pay-mark.svg" alt="Apple Pay" /></span>
                <span className="sbr-payment-logo"><img src="/payment-logos/google-pay-mark.svg" alt="Google Pay" /></span>
                <span className="sbr-payment-logo"><img src="/payment-logos/paypal-mark.svg" alt="PayPal" /></span>
                <span className="sbr-payment-logo"><img src="/payment-logos/visa-mark.svg" alt="Visa" /></span>
                <span className="sbr-payment-logo"><img src="/payment-logos/mastercard-mark.svg" alt="Mastercard" /></span>
              </div>
            ) : (
              <div className="badges"><span className="badge">◇ SSL SECURED</span><span className="badge">▣ SAFE PAYMENT</span><span className="badge">⌁ OFFICIAL DATA</span></div>
            )}
          </div>
        </section>
      </main>

      {showServiceHistory && brand.key === "servicelad" && (
        <div className="service-modal-backdrop" role="presentation" onClick={() => setShowServiceHistory(false)}>
          <section className="service-modal" role="dialog" aria-modal="true" aria-labelledby="service-modal-title" onClick={(event) => event.stopPropagation()}>
            <button className="service-modal-close" type="button" onClick={() => setShowServiceHistory(false)} aria-label="Close">×</button>
            <div className="eyebrow">Inside the record</div>
            <h2 id="service-modal-title">See the miles between the milestones.</h2>
            <p>ServiceLad brings the important signals together so you can read a car's history at a glance.</p>
            <ul>
              <li><strong>Service dates</strong><span>See when the work happened.</span></li>
              <li><strong>Mileage trail</strong><span>Follow the numbers and spot gaps.</span></li>
              <li><strong>Garage details</strong><span>Know who looked after the car.</span></li>
              <li><strong>Work recorded</strong><span>Understand what was carried out.</span></li>
            </ul>
            <button className="service-modal-action" type="button" onClick={() => { setShowServiceHistory(false); document.querySelector("#vrm")?.focus(); }}>Check a vehicle <span>→</span></button>
          </section>
        </div>
      )}

      {activeRisk !== null && brand.key === "servicebyreg" && (
        <div className="risk-modal-backdrop" role="presentation" onClick={() => setActiveRisk(null)}>
          <section
            className="risk-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="risk-modal-title"
            onClick={(event) => event.stopPropagation()}
          >
            <button className="risk-modal-close" type="button" onClick={() => setActiveRisk(null)} aria-label="Close">×</button>
            <div className="risk-modal-index">CHECK {String(activeRisk + 1).padStart(2, "0")} / {String(riskFeatures.length).padStart(2, "0")}</div>
            <span className="risk-modal-watermark" aria-hidden="true">
              <RiskCheckIcon name={riskFeatures[activeRisk].id} size="watermark" />
            </span>
            <div className="risk-modal-head">
              <div className="risk-modal-badge" aria-hidden="true">
                <RiskCheckIcon name={riskFeatures[activeRisk].id} size="lg" />
              </div>
              <span className={`risk-modal-tag${riskDetails[activeRisk].tag === "RISK CHECK" ? " is-risk" : ""}`}>{riskDetails[activeRisk].tag}</span>
            </div>
            <h2 id="risk-modal-title">{riskFeatures[activeRisk].title}</h2>
            <p className="risk-modal-body">{riskDetails[activeRisk].body}</p>
            <div className="risk-modal-why"><span>Why it matters</span><p>{riskDetails[activeRisk].why}</p></div>
            <div className="risk-modal-tabs" role="tablist" aria-label="Other checks in this report">
              {riskFeatures.map((feature, i) => (
                <button
                  type="button"
                  key={feature.id}
                  role="tab"
                  aria-selected={i === activeRisk}
                  aria-label={feature.title}
                  className={`risk-modal-tab${i === activeRisk ? " is-active" : ""}`}
                  onClick={() => setActiveRisk(i)}
                >
                  <RiskCheckIcon name={feature.id} size="sm" />
                </button>
              ))}
            </div>
          </section>
        </div>
      )}

      {brand.key === "servicebyreg" ? (
        <SbrFooter brand={brand} isHomepage />
      ) : (
        <footer>
          <div className="wrap">
            <div className="footer-top"><Brand brand={brand} light /><div className="footer-links"><a href="/car-tax-calculator/">{brand.key === "servicebyreg" ? "Road tax check" : brand.key === "servicelad" ? "Check road tax" : "Tax calculator"}</a><a href="/insurance-groups">Insurance group</a><a href="/free-car-check">Free car check</a><a href="#features">What&apos;s included</a><a href="#pricing">Pricing</a>{brand.guides?.length > 0 && <a href="/guides/">Guides</a>}<a href="/about">About</a><a href="/contact">Contact</a><a href="/terms">Terms</a><a href="/privacy">Privacy</a></div></div>
            <div className="footer-bottom">
              <span>© 2026 {brand.name}. All rights reserved.</span>
              <span>Vehicle data comes from trusted third parties. {brand.name} is independent and unaffiliated with DVLA or DVSA.</span>
            </div>
          </div>
        </footer>
      )}
    </>
  );
}
